import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { Play, Square, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const fmt = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${h ? h + ":" : ""}${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const TimerWidget = () => {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [desc, setDesc] = useState("");
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const startRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    api.get("/projects?limit=100").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  // restore timer from sessionStorage across nav
  useEffect(() => {
    const saved = sessionStorage.getItem("timer_start");
    if (saved) {
      startRef.current = Number(saved);
      setProjectId(sessionStorage.getItem("timer_project") || "");
      setDesc(sessionStorage.getItem("timer_desc") || "");
      setRunning(true);
    }
  }, []);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const start = () => {
    if (!projectId) return toast.error("Select a project first");
    startRef.current = Date.now();
    sessionStorage.setItem("timer_start", String(startRef.current));
    sessionStorage.setItem("timer_project", projectId);
    sessionStorage.setItem("timer_desc", desc);
    setElapsed(0);
    setRunning(true);
    setOpen(false);
  };

  const stop = useCallback(async () => {
    clearInterval(intervalRef.current);
    setRunning(false);
    // Recover projectId from sessionStorage if state was lost across navigation
    const pid = projectId || sessionStorage.getItem("timer_project") || "";
    if (!pid) {
      toast.error("No project selected — time not saved");
      setElapsed(0);
      sessionStorage.removeItem("timer_start");
      sessionStorage.removeItem("timer_project");
      sessionStorage.removeItem("timer_desc");
      return;
    }
    const hours = Math.max(elapsed / 3600, 0.1);
    setSaving(true);
    try {
      await api.post(`/projects/${pid}/time-logs`, {
        hours_spent: parseFloat(hours.toFixed(2)),
        notes: desc || sessionStorage.getItem("timer_desc") || "Timer entry",
        date_logged: new Date().toISOString().slice(0, 10),
      });
      toast.success(`Logged ${fmt(elapsed)}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "unknown error";
      toast.error(`Failed to save time log: ${detail}`);
    } finally {
      setSaving(false);
      setElapsed(0);
      sessionStorage.removeItem("timer_start");
      sessionStorage.removeItem("timer_project");
      sessionStorage.removeItem("timer_desc");
    }
  }, [elapsed, projectId, desc]);

  const projName = projects.find((p) => p.project_id === projectId)?.name;

  return (
    <div className="relative">
      {running ? (
        <div className="flex items-center gap-1.5">
          <div className="flex items-center gap-1 px-2 py-1 border border-[#4F46E5]/30 bg-[#4F46E5]/5 rounded-none">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4F46E5] animate-pulse" />
            <span className="text-xs font-mono text-[#4F46E5]">{fmt(elapsed)}</span>
            {projName && <span className="text-[10px] text-gray-500 hidden sm:inline ml-1">{projName}</span>}
          </div>
          <button
            onClick={stop}
            disabled={saving}
            className="h-7 w-7 flex items-center justify-center border border-[#FF007A]/40 bg-[#FF007A]/10 text-[#FF007A] hover:bg-[#FF007A]/20 transition-colors"
            title="Stop and log time"
            data-testid="timer-stop-btn"
          >
            <Square className="w-3 h-3 fill-current" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1 h-7 px-2 border border-gray-200 text-gray-500 hover:text-gray-900 hover:border-gray-400 transition-colors text-xs"
          data-testid="timer-open-btn"
          title="Start timer"
        >
          <Play className="w-3 h-3" />
          <span className="hidden sm:inline">Timer</span>
          <ChevronDown className="w-3 h-3" />
        </button>
      )}

      {open && !running && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-9 z-50 w-64 border border-[#374151] bg-[#111827] shadow-xl p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">Start timer</div>
            <div>
              <div className="text-[10px] text-[#6B7280] mb-1">Project</div>
              <select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className="w-full h-8 rounded-none border border-[#374151] bg-[#030712] text-white px-2 text-xs"
                data-testid="timer-project-select"
              >
                <option value="">— select project —</option>
                {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] text-[#6B7280] mb-1">What are you working on?</div>
              <input
                type="text"
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && start()}
                placeholder="Description (optional)"
                className="w-full h-8 rounded-none border border-[#374151] bg-[#030712] text-white px-2 text-xs"
                data-testid="timer-desc-input"
              />
            </div>
            <button
              onClick={start}
              className="w-full h-8 flex items-center justify-center gap-2 bg-[#002FA7] hover:bg-[#002480] text-white text-xs transition-colors"
              data-testid="timer-start-btn"
            >
              <Play className="w-3 h-3 fill-current" /> Start
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default TimerWidget;
